import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { 
  Mic, User, LogOut, Settings, Phone, Clock, Building2, 
  TrendingUp, Target, Calendar, ExternalLink, Plus,
  ChevronRight, Users, Eye, GitBranch
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getScoreColor(score) {
  if (score >= 70) return 'text-green-600';
  if (score >= 40) return 'text-yellow-600';
  return 'text-red-600';
}

export default function CallHistory() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  const [sessions, setSessions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [frameworks, setFrameworks] = useState({});
  const [showAllCalls, setShowAllCalls] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('all');
  const [usersList, setUsersList] = useState([]);

  useEffect(() => {
    fetchData();
  }, [showAllCalls, selectedUserId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Build query params
      const historyParams = new URLSearchParams({ limit: '50' });
      const analyticsParams = new URLSearchParams({ days: '30' });
      
      if (isAdmin && showAllCalls) {
        historyParams.set('show_all', 'true');
        analyticsParams.set('show_all', 'true');
        if (selectedUserId && selectedUserId !== 'all') {
          historyParams.set('user_id', selectedUserId);
          analyticsParams.set('user_id', selectedUserId);
        }
      }
      
      const [historyRes, analyticsRes, frameworksRes] = await Promise.all([
        axios.get(`${API}/sessions/history?${historyParams}`),
        axios.get(`${API}/analytics/overview?${analyticsParams}`),
        axios.get(`${API}/frameworks`)
      ]);
      
      setSessions(historyRes.data.sessions || []);
      setAnalytics(analyticsRes.data);
      
      // Get users list for admin filter
      if (historyRes.data.users) {
        setUsersList(historyRes.data.users);
      }
      
      // Build framework lookup
      const fwMap = {};
      (frameworksRes.data || []).forEach(fw => {
        fwMap[fw.id] = fw.name;
      });
      setFrameworks(fwMap);
    } catch (e) {
      console.error('Failed to fetch data:', e);
      toast.error('Failed to load call history');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getOverallScore = (scores) => {
    if (!scores || Object.keys(scores).length === 0) return 0;
    return Math.round(Object.values(scores).reduce((a, b) => a + b, 0) / Object.keys(scores).length);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="call-history-page">
      {/* Header */}
      <header className="h-16 border-b border-zinc-200 flex items-center justify-between px-6 bg-white">
        <div className="flex items-center gap-4">
          <Link to={user?.role === 'admin' ? '/admin' : '/'} className="flex items-center gap-2">
            <img src="/pipeguide-logo.png" alt="PipeGuide" className="h-8 w-8 object-contain" />
            <span className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
              PipeGuide
            </span>
          </Link>
          <span className="text-zinc-300">|</span>
          <span className="text-zinc-600 font-medium">
            {isAdmin && showAllCalls ? 'Team Calls' : 'My Calls'}
          </span>
        </div>

        <div className="flex items-center gap-3">
          {/* Admin: Toggle to view all calls */}
          {isAdmin && (
            <div className="flex items-center gap-2 bg-zinc-100 rounded-md px-3 py-1.5">
              <Eye className="w-4 h-4 text-zinc-500" />
              <span className="text-sm text-zinc-600">View all</span>
              <Switch 
                checked={showAllCalls} 
                onCheckedChange={setShowAllCalls}
                data-testid="toggle-view-all"
              />
            </div>
          )}
          
          {/* Admin: Filter by user */}
          {isAdmin && showAllCalls && usersList.length > 0 && (
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger className="w-[180px] rounded-md" data-testid="user-filter">
                <SelectValue placeholder="All Reps" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Reps</SelectItem>
                {usersList.map(u => (
                  <SelectItem key={u.email} value={u.email}>
                    {u.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          <Button asChild variant="default" className="rounded-md bg-brand-primary hover:bg-brand-hover">
            <Link to="/" data-testid="new-call-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Call
            </Link>
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="rounded-md" data-testid="user-menu-btn">
                <User className="w-4 h-4 mr-2" />
                {user?.name}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem disabled>
                <span className="text-zinc-500">{user?.email}</span>
              </DropdownMenuItem>
              <DropdownMenuItem disabled>
                <Badge variant="outline" className="text-xs">
                  {user?.role}
                </Badge>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {isAdmin && (
                <DropdownMenuItem asChild>
                  <Link to="/admin">
                    <Settings className="w-4 h-4 mr-2" />
                    Admin Panel
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Analytics Overview */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="analytics-overview">
            <Card className="border-zinc-200 shadow-none rounded-md">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Calls</p>
                    <p className="text-3xl font-bold mt-1" style={{ fontFamily: 'var(--font-mono)' }}>
                      {analytics.total_calls}
                    </p>
                  </div>
                  <Phone className="w-8 h-8 text-brand-primary" />
                </div>
                <p className="text-xs text-zinc-500 mt-2">
                  {analytics.completed_calls} completed
                </p>
              </CardContent>
            </Card>

            <Card className="border-zinc-200 shadow-none rounded-md">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Avg Score</p>
                    <p className={`text-3xl font-bold mt-1 ${getScoreColor(analytics.avg_qualification_score)}`} style={{ fontFamily: 'var(--font-mono)' }}>
                      {analytics.avg_qualification_score}%
                    </p>
                  </div>
                  <Target className="w-8 h-8 text-green-500" />
                </div>
                <Progress 
                  value={analytics.avg_qualification_score} 
                  className="h-1.5 mt-3 rounded-none"
                />
              </CardContent>
            </Card>

            <Card className="border-zinc-200 shadow-none rounded-md col-span-1 md:col-span-2">
              <CardContent className="pt-6">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-3">
                  {isAdmin && showAllCalls && analytics.rep_performance?.length > 0 ? 'Rep Performance' : 'Category Averages'}
                </p>
                
                {/* Show rep performance for admins, category averages for reps */}
                {isAdmin && showAllCalls && analytics.rep_performance?.length > 0 ? (
                  <div className="grid grid-cols-2 gap-2">
                    {analytics.rep_performance.slice(0, 4).map((rep) => (
                      <div key={rep.user_id} className="flex items-center justify-between bg-zinc-50 rounded px-3 py-2">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-zinc-400" />
                          <span className="text-sm text-zinc-700 truncate">{rep.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-zinc-500">{rep.total_calls} calls</span>
                          <span className={`text-sm font-mono font-bold ${getScoreColor(rep.avg_score)}`}>
                            {rep.avg_score}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(analytics.category_averages || {}).slice(0, 4).map(([cat, score]) => (
                      <div key={cat} className="flex items-center justify-between bg-zinc-50 rounded px-3 py-2">
                        <span className="text-sm text-zinc-700">{cat}</span>
                        <span className={`text-sm font-mono font-bold ${getScoreColor(score)}`}>
                          {score}%
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Calls List */}
        <Card className="border-zinc-200 shadow-none rounded-md">
          <CardHeader className="border-b border-zinc-200">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg" style={{ fontFamily: 'var(--font-heading)' }}>
                {isAdmin && showAllCalls ? 'Team Calls' : 'My Calls'}
              </CardTitle>
              <Badge variant="outline" className="text-xs">
                {sessions.length} calls
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : sessions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Phone className="w-12 h-12 text-zinc-300 mb-4" />
                <h3 className="text-lg font-medium text-zinc-900 mb-2">No calls yet</h3>
                <p className="text-zinc-500 mb-4">Start your first qualification call to see history here</p>
                <Button asChild className="rounded-md bg-brand-primary hover:bg-brand-hover">
                  <Link to="/">
                    <Plus className="w-4 h-4 mr-2" />
                    Start a Call
                  </Link>
                </Button>
              </div>
            ) : (
              <ScrollArea className="h-[500px]">
                <div className="divide-y divide-zinc-100">
                  {sessions.map((session) => {
                    const overallScore = getOverallScore(session.qualification_scores);
                    
                    return (
                      <Link 
                        key={session.id}
                        to={`/call/${session.id}`}
                        className="flex items-center justify-between p-4 hover:bg-zinc-50 transition-colors group"
                        data-testid={`call-item-${session.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            session.status === 'completed' ? 'bg-green-100' : 'bg-yellow-100'
                          }`}>
                            <Phone className={`w-5 h-5 ${
                              session.status === 'completed' ? 'text-green-600' : 'text-yellow-600'
                            }`} />
                          </div>
                          
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-zinc-900">
                                {session.prospect_name || 'Unknown Prospect'}
                              </span>
                              {session.company_name && (
                                <span className="text-zinc-500 text-sm">
                                  @ {session.company_name}
                                </span>
                              )}
                              {session.call_number > 1 && (
                                <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700">
                                  Call #{session.call_number}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-sm text-zinc-500 mt-1">
                              {/* Show rep name for admin view */}
                              {isAdmin && showAllCalls && session.user_name && (
                                <span className="flex items-center gap-1 text-brand-primary">
                                  <User className="w-3 h-3" />
                                  {session.user_name}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDate(session.created_at)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatTime(session.created_at)}
                              </span>
                              <Badge variant="outline" className="text-xs">
                                {frameworks[session.framework_id] || session.framework_id}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className={`text-lg font-bold font-mono ${getScoreColor(overallScore)}`}>
                              {overallScore}%
                            </div>
                            <div className="flex items-center gap-2 text-xs text-zinc-500">
                              <span>{session.transcript_count} messages</span>
                              {session.follow_up_count > 0 && (
                                <Badge variant="outline" className="text-xs bg-purple-50 text-purple-600">
                                  <GitBranch className="w-3 h-3 mr-1" />
                                  {session.follow_up_count}
                                </Badge>
                              )}
                              {session.has_summary && (
                                <Badge variant="outline" className="text-xs bg-brand-muted text-brand-primary">
                                  Summary
                                </Badge>
                              )}
                              {session.crm_link && (
                                <ExternalLink className="w-3 h-3 text-green-500" />
                              )}
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-zinc-300 group-hover:text-zinc-500 transition-colors" />
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
